import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaKey } from 'react-icons/fa';
import { useAuth } from '../../../context/AuthContext';
import './Login.css';

const ROLE_HOME = {
  superadmin:   '/superadmin',
  admin:        '/admin',
  clerk:        '/clerk',
  collegeadmin: '/collegeadmin',
};

const Login = () => {
  const [form, setForm]                 = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]               = useState('');
  const [loading, setLoading]           = useState(false);
  const { login }                       = useAuth();
  const navigate                        = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(form.email.trim(), form.password);
      navigate(ROLE_HOME[user.role] || '/admin', { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || err.message || 'Invalid credentials. Please check your email/ID and password.');
    } finally {
      setLoading(false);
    }
  };

  const togglePassword = () => setShowPassword(prev => !prev);

  return (
    <div className="login-warm-container">
      
      {/* ── Ambient Warm Lights ── */}
      <div className="login-ambient blob-amber"></div>
      <div className="login-ambient blob-peach"></div>
      <div className="login-ambient blob-sand"></div>

      {/* ── Intellectual Puzzle & Math Background ── */}
      <div className="login-intellect-bg">
        
        {/* Geometric Network Lines */}
        <div className="intellect-line line-1"></div>
        <div className="intellect-line line-2"></div>
        <div className="intellect-line line-3"></div>
        
        {/* Abstract Math & Cryptography Nodes */}
        <div className="intellect-node math-tl">
          <div className="math-equation">e<sup>iπ</sup> + 1 = 0</div>
          <div className="math-caption">Euler's Identity</div>
        </div>

        <div className="intellect-node math-tr">
          <div className="math-equation">H(k) = Σ (c<sub>i</sub> · x<sup>i</sup>) mod P</div>
          <div className="math-caption">Cryptographic Hashing</div>
        </div>

        <div className="intellect-node math-bl">
          <div className="math-equation">∇ × E = - ∂B / ∂t</div>
          <div className="math-caption">Maxwell's Matrix</div>
        </div>

        <div className="intellect-node math-br">
          <div className="math-equation">φ = (1 + √5) / 2</div>
          <div className="math-caption">Golden Ratio Subsystem</div>
        </div>

        {/* Floating Puzzle/Geometric Shapes */}
        <div className="geo-shape geo-circle"></div>
        <div className="geo-shape geo-diamond"></div>
        <div className="geo-shape geo-hexagon"></div>
        
        {/* Subtle Text Texture */}
        <div className="texture-text text-left">
          // Initializing secure encryption matrix...<br/>
          // Validating zero-knowledge proofs...<br/>
          // Establishing end-to-end tunnel...
        </div>
      </div>

      {/* ── Centered Login Card ── */}
      <div className="login-glass-card">
        <div className="login-card-header">
          <div className="login-key-icon">
            <FaKey />
          </div>
          <h2>Secure Access</h2>
          <p>Please authenticate to access the workspace.</p>
        </div>

        {error && (
          <div className="login-alert-error">
            <span>⚠</span> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="login-input-group">
            <label>Email / User ID</label>
            <div className="login-input-wrapper">
              <FaEnvelope className="login-icon" />
              <input
                type="text"
                required
                autoFocus
                placeholder="Enter your email or ID"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
              />
            </div>
          </div>

          <div className="login-input-group">
            <label>Password</label>
            <div className="login-input-wrapper">
              <FaLock className="login-icon" />
              <input
                type={showPassword ? "text" : "password"}
                required
                placeholder="••••••••••••"
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
              />
              <button 
                type="button" 
                className="login-toggle-eye" 
                onClick={togglePassword}
                tabIndex="-1"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <button type="submit" className="login-submit-btn" disabled={loading}>
            {loading ? (
              <><span className="login-loading-spinner" /> Authenticating...</>
            ) : (
              'Sign In'
            )}
          </button>
          
          <div className="login-card-footer">
            256-bit Encrypted Connection
          </div>
        </form>
      </div>

    </div>
  );
};

export default Login;